import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRotateLeft, faArrowUpRightFromSquare, faCircleInfo, faLock } from "@fortawesome/free-solid-svg-icons";

import Alert from "../../crmpostventa-shared/components/Alert";
import { formatCampoValor, isLocked, isTramite } from "../fichaFormat";
import { RubroSearchSelect, buildFichaCatalogOptions, fichaOptionSource, withCurrentOption } from "./fichaOptions";
import { plural } from "./fichaEditorModel";

export const controlId = (clave) => `crm-ficha-fe-${clave}`;

const TELEFONOS = new Set(["telefono", "telefonoAlternativo"]);

function Control({ campo, value, invalid, describedBy, disabled, catalogos, onChange, onBlur }) {
  const id = controlId(campo.clave);
  const aria = { "aria-invalid": invalid || undefined, "aria-describedby": describedBy };

  if (isLocked(campo)) {
    return <input className="crm-rollo__input" id={id} value={formatCampoValor({ ...campo, valor: value })} disabled aria-describedby={describedBy} readOnly />;
  }

  const source = fichaOptionSource({ key: campo.clave, dataSource: campo.fuenteDatos });
  if (source === "rubros") {
    return (
      <RubroSearchSelect
        inputId={id}
        options={catalogos?.rubros || []}
        value={value}
        isDisabled={disabled}
        ariaDescribedBy={describedBy}
        onChange={onChange}
      />
    );
  }

  if (source) {
    const options = withCurrentOption(buildFichaCatalogOptions(catalogos, { key: campo.clave, dataSource: campo.fuenteDatos }), value);
    return (
      <span className="crm-rollo__field crm-rollo__field--select">
        <select className="crm-rollo__select" id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} onBlur={onBlur} {...aria}>
          <option value="">Sin dato</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
    );
  }

  return (
    <input
      className="crm-rollo__input"
      id={id}
      type={campo.formato === "FECHA" ? "date" : campo.clave === "mail" ? "email" : "text"}
      inputMode={TELEFONOS.has(campo.clave) ? "tel" : undefined}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      {...aria}
    />
  );
}

function EditorField({ campo, value, original, modificado, automatico, error, remote, disabled, catalogos, onChange, onBlur, onUndo }) {
  const id = controlId(campo.clave);
  const helpId = `${id}-h`;
  const locked = isLocked(campo);
  const vaciado = modificado && !automatico && !String(value ?? "").trim() && original;

  let help = null;
  if (error) {
    help = (
      <span className="crm-rollo__err" id={helpId}>
        {error}
      </span>
    );
  } else if (automatico && !locked) {
    help = (
      <span className="crm-rollo__help" id={helpId}>
        Se completó solo con el Rubro
      </span>
    );
  } else if (vaciado) {
    help = (
      <span className="crm-rollo__help" id={helpId}>
        Queda vacío. Antes: {formatCampoValor({ ...campo, valor: original })}
      </span>
    );
  } else if (campo.ayuda) {
    help = (
      <span className="crm-rollo__help" id={helpId}>
        {locked ? <FontAwesomeIcon icon={faLock} aria-hidden="true" className="crm-ficha__fe-lock" /> : null}
        {campo.ayuda}
      </span>
    );
  }

  const className = [
    "crm-rollo__fld",
    "crm-ficha__fe-field",
    modificado ? "is-mod" : "",
    remote ? "is-remote" : "",
    error ? "has-error" : "",
    locked ? "is-ro" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={className} data-campo={campo.clave}>
      <div className="crm-ficha__fe-lrow">
        <label className="crm-rollo__fld-label" htmlFor={id}>
          {modificado ? <span className="crm-ficha__fe-dot" aria-hidden="true" /> : null}
          {campo.etiqueta}
          {modificado ? <span className="crm-rollo__sr-only"> (modificado)</span> : null}
        </label>
        {modificado && !automatico && !locked ? (
          <button
            type="button"
            className="crm-rollo__link crm-ficha__fe-undo"
            aria-label={`Deshacer el cambio en ${campo.etiqueta}`}
            disabled={disabled}
            onClick={() => onUndo(campo.clave)}
          >
            <FontAwesomeIcon icon={faArrowRotateLeft} aria-hidden="true" />
            Deshacer
          </button>
        ) : null}
      </div>
      <Control
        campo={campo}
        value={value}
        invalid={Boolean(error)}
        describedBy={help ? helpId : undefined}
        disabled={disabled}
        catalogos={catalogos}
        onChange={(next) => onChange(campo.clave, next)}
        onBlur={() => onBlur(campo.clave)}
      />
      {help}
      {remote ? (
        <span className="crm-ficha__fe-remote">
          <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" />
          {remote}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Paso 1 de 2 of the Ficha editor (design Prompt 2): one block per section in card order, trámite fields apart with
 * "Ir a Altas/Bajas", the gestión notice when the asesor came from a gestión and the notice of the fields another asesor
 * changed (after "Ver cambios nuevos").
 */
export default function EditorStep({
  secciones,
  estado,
  originales,
  errorDe,
  remote,
  aviso,
  gestionTitle,
  gestionHref,
  altasHref,
  disabled,
  catalogos,
  onChange,
  onBlur,
  onUndo,
  onLink,
}) {
  const modificados = new Set(estado.cambios.map((cambio) => cambio.campo.clave));
  const remoteKeys = new Set(remote?.keys || []);

  const renderField = (campo) => (
    <EditorField
      key={campo.clave}
      campo={campo}
      value={estado.valores[campo.clave] ?? ""}
      original={originales[campo.clave]}
      modificado={modificados.has(campo.clave)}
      automatico={estado.automaticos.has(campo.clave)}
      error={errorDe(campo.clave)}
      remote={remoteKeys.has(campo.clave) ? remote.nota : null}
      disabled={disabled}
      catalogos={catalogos}
      onChange={onChange}
      onBlur={onBlur}
      onUndo={onUndo}
    />
  );

  return (
    <>
      {gestionTitle ? (
        <div className="crm-ficha__fe-notice">
          <Alert tone="info" role="status" title={gestionTitle} />
          <a className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" href={gestionHref} onClick={(event) => onLink(event, gestionHref)}>
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
            Volver a la gestión
          </a>
        </div>
      ) : null}
      {remote ? (
        <Alert tone="info" role="status" title={remote.titulo}>
          Los marcamos abajo con los datos nuevos. Tus cambios se mantienen: revisalos antes de guardar.
        </Alert>
      ) : null}
      {aviso ? <Alert tone="error" role="alert" title={aviso} /> : null}

      {secciones.map((seccion) => {
        const campos = seccion.campos || [];
        const propios = campos.filter((campo) => !isTramite(campo));
        const tramite = campos.filter(isTramite);
        const cambios = propios.filter((campo) => modificados.has(campo.clave) && !isLocked(campo)).length;
        const titleId = `crm-ficha-fes-${seccion.clave}`;
        return (
          <section key={seccion.clave} className="crm-ficha__fe-sec" aria-labelledby={titleId}>
            <div className="crm-ficha__fe-shead">
              <h3 id={titleId}>{seccion.titulo}</h3>
              {cambios ? <span className="crm-ficha__fe-count">{plural(cambios, "cambio", "cambios")}</span> : null}
            </div>
            <div className="crm-ficha__fe-grid">{propios.map(renderField)}</div>
            {tramite.length ? (
              <div className="crm-ficha__fe-tramite">
                <div className="crm-ficha__tramite-head">
                  <span>Se cambian con un trámite</span>
                  <a className="crm-rollo__link crm-ficha__tramite-link" href={altasHref} onClick={(event) => onLink(event, altasHref)}>
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" />
                    Ir a Altas/Bajas
                  </a>
                </div>
                <div className="crm-ficha__fe-grid">{tramite.map(renderField)}</div>
              </div>
            ) : null}
          </section>
        );
      })}
    </>
  );
}
